import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Download, ShieldCheck } from 'lucide-react';
import { Button, Input, Label } from '@clarix/ui';
import { downloadBackupCodesPdf } from '@clarix/ui-shared';
import type { LoginResponse, LoginResult } from '@clarix/shared-types';
import {
  useVerifyLoginTwoFactor,
  useSetupTwoFactor,
  useEnableTwoFactor,
  useCompleteEnrolment,
  useSkipEnrolment,
  useChangePassword,
  getApiErrorMessage,
} from '@/core/api';
import { PasswordInput } from './PasswordInput';
import { PasswordErrorMessage } from './PasswordErrorMessage';

/**
 * The follow-up steps a /login (or /login-with-otp) can branch into when the
 * result is a gated LoginResult instead of a full session:
 *   • TwoFactorChallengeForm — the account has 2FA; redeem a code.
 *   • EnrolTwoFactorForm     — 2FA is compulsory but not set up; enrol now.
 *   • ChangePasswordForm     — the password is stale; change it, then re-sign-in.
 *
 * All three render in place on the login card (no persisted session, no route
 * change) — the limited enrolment/change tokens are held in component state and
 * sent per-request, so RequireUnauthed never bounces the user mid-flow.
 */

const ERROR_BOX =
  'rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300';

function GateHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6 flex flex-col items-center text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
        <ShieldCheck className="h-6 w-6" strokeWidth={1.75} />
      </span>
      <h1 className="text-5xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <p className="mt-1 text-xl text-slate-500">{subtitle}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 2FA challenge — the account is enrolled; redeem an authenticator/backup code.
// -----------------------------------------------------------------------------

export function TwoFactorChallengeForm({
  challengeToken,
  workspaceSlug,
  onResult,
  onBack,
}: {
  challengeToken: string;
  workspaceSlug?: string | null;
  onResult: (data: LoginResult) => void;
  onBack: () => void;
}) {
  const verify = useVerifyLoginTwoFactor();
  const [code, setCode] = useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = await verify.mutateAsync({
        challenge_token: challengeToken,
        code: code.trim(),
        ...(workspaceSlug ? { workspace_slug: workspaceSlug } : {}),
      });
      onResult(data);
    } catch {
      /* surfaced below */
    }
  };

  const error = verify.error
    ? getApiErrorMessage(verify.error, "Couldn't verify the code. Please try again.")
    : null;

  return (
    <div>
      <GateHeading
        title="Two-factor authentication"
        subtitle="Enter the 6-digit code from your authenticator app to finish signing in."
      />
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <Label htmlFor="twofa-code">Authentication code</Label>
          <Input
            id="twofa-code"
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <p className="mt-1 text-lg text-slate-500">
            Lost your device? Enter one of your saved backup codes instead. No backup codes? Ask
            your workspace owner or admin to reset your two-factor authentication.
          </p>
        </div>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <Button type="submit" className="w-full" disabled={verify.isPending || code.trim().length < 6}>
          {verify.isPending ? 'Verifying…' : 'Verify & sign in'}
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="w-full text-center text-xl text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Back to sign in
        </button>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Forced 2FA enrolment — compulsory setup: QR → confirm code → backup codes.
// -----------------------------------------------------------------------------

type EnrolPhase = 'intro' | 'enrolling' | 'backup-codes';

export function EnrolTwoFactorForm({
  token,
  workspaceSlug,
  canSkip = false,
  onComplete,
}: {
  token: string;
  workspaceSlug?: string | null;
  /**
   * true → 2FA is OPTIONAL and this is the one-time offer: show "Skip for now".
   * Defaults false so a caller that forgets to pass it renders the compulsory
   * (no way out) form rather than accidentally offering an escape.
   */
  canSkip?: boolean;
  onComplete: (data: LoginResponse) => void;
}) {
  const setup = useSetupTwoFactor();
  const enable = useEnableTwoFactor();
  const complete = useCompleteEnrolment();
  const skip = useSkipEnrolment();

  const [phase, setPhase] = useState<EnrolPhase>('intro');
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const beginEnrol = async () => {
    try {
      const res = await setup.mutateAsync(token);
      setSecret(res.secret);
      setOtpauthUrl(res.otpauth_url);
      setCode('');
      setPhase('enrolling');
    } catch {
      /* surfaced below */
    }
  };

  const confirmEnrol = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await enable.mutateAsync({ token, code: code.trim() });
      setBackupCodes(res.backup_codes);
      setPhase('backup-codes');
    } catch {
      /* surfaced below */
    }
  };

  const finish = async () => {
    try {
      const data = await complete.mutateAsync({ token, workspaceSlug });
      onComplete(data);
    } catch {
      /* surfaced below */
    }
  };

  const skipEnrol = async () => {
    try {
      const data = await skip.mutateAsync({ token, workspaceSlug });
      onComplete(data);
    } catch {
      /* surfaced below */
    }
  };

  const setupErr = setup.error ? getApiErrorMessage(setup.error, "Couldn't start setup.") : null;
  const skipErr = skip.error
    ? getApiErrorMessage(skip.error, "Couldn't skip setup. Please try again.")
    : null;
  const enableErr = enable.error
    ? getApiErrorMessage(enable.error, "Couldn't verify that code. Please try again.")
    : null;
  const completeErr = complete.error
    ? getApiErrorMessage(complete.error, "Couldn't finish setup. Please try again.")
    : null;

  return (
    <div>
      <GateHeading
        title={canSkip ? 'Add an extra layer of security' : 'Set up two-factor authentication'}
        subtitle={
          canSkip
            ? 'Protect your account with an authenticator app. You can do this later from your profile.'
            : 'Your workspace requires 2FA. Set it up now to continue.'
        }
      />

      {phase === 'intro' && (
        <div className="space-y-4">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            Use an authenticator app (Google Authenticator, 1Password, Authy…). You&apos;ll scan a QR
            code and enter a 6-digit code to confirm.
          </p>
          {setupErr && <div className={ERROR_BOX}>{setupErr}</div>}
          {skipErr && <div className={ERROR_BOX}>{skipErr}</div>}
          <Button
            className="w-full"
            onClick={beginEnrol}
            disabled={setup.isPending || skip.isPending}
          >
            {setup.isPending ? 'Starting…' : 'Begin setup'}
          </Button>
          {/* Only when the server said 2FA is optional here. It re-checks on
              /auth/2fa/skip-enrolment, so this button can never bypass a
              compulsory policy even if canSkip were wrong. */}
          {canSkip && (
            <button
              type="button"
              onClick={skipEnrol}
              disabled={skip.isPending || setup.isPending}
              className="w-full text-center text-xl text-indigo-600 hover:underline disabled:opacity-60 dark:text-indigo-400"
            >
              {skip.isPending ? 'Skipping…' : 'Skip for now'}
            </button>
          )}
        </div>
      )}

      {phase === 'enrolling' && (
        <form onSubmit={confirmEnrol} className="space-y-4">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            1. Scan this QR code in your authenticator app (or enter the key manually).
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            <div className="rounded-lg bg-white p-3">
              {otpauthUrl && <QRCodeSVG value={otpauthUrl} size={144} />}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
                Manual entry key
              </p>
              <code className="block break-all text-lg text-slate-900 dark:text-slate-100">
                {secret}
              </code>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="enrol-code">2. Enter the 6-digit code it shows</Label>
            <Input
              id="enrol-code"
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {enableErr && <div className={ERROR_BOX}>{enableErr}</div>}
          </div>
          <Button type="submit" className="w-full" disabled={enable.isPending || code.trim().length !== 6}>
            {enable.isPending ? 'Verifying…' : 'Verify & enable'}
          </Button>
        </form>
      )}

      {phase === 'backup-codes' && (
        <BackupCodes
          codes={backupCodes}
          accountLabel={workspaceSlug}
          finishing={complete.isPending}
          error={completeErr}
          onDone={finish}
        />
      )}
    </div>
  );
}

function BackupCodes({
  codes,
  accountLabel,
  finishing,
  error,
  onDone,
}: {
  codes: string[];
  accountLabel?: string | null;
  finishing: boolean;
  error: string | null;
  onDone: () => void;
}) {
  const [saved, setSaved] = useState(false);

  const savePdf = () => {
    if (!downloadBackupCodesPdf({ codes, accountLabel })) return;
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-amber-50 px-3 py-2 text-xl text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
        2FA is on. Download these backup codes and keep the file somewhere safe — each works once
        and they are shown <strong>only now</strong>.
      </div>
      <ul className="grid grid-cols-2 gap-1.5 rounded-lg bg-slate-50 p-3 font-mono text-xl dark:bg-slate-900">
        {codes.map((c) => (
          <li key={c} className="tabular-nums text-slate-900 dark:text-slate-100">
            {c}
          </li>
        ))}
      </ul>
      {error && <div className={ERROR_BOX}>{error}</div>}
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={savePdf} className="gap-1.5">
          {saved ? (
            <Check className="h-4 w-4" strokeWidth={1.5} />
          ) : (
            <Download className="h-4 w-4" strokeWidth={1.5} />
          )}
          {saved ? 'Downloaded' : 'Download PDF'}
        </Button>
        <Button type="button" className="flex-1" onClick={onDone} disabled={finishing}>
          {finishing ? 'Finishing…' : "I've saved them — continue"}
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Forced password change — the verified password no longer meets policy.
// -----------------------------------------------------------------------------

const CHANGE_REASON: Record<'POLICY' | 'ROTATION', string> = {
  POLICY: 'Your password no longer meets the security policy. Set a new one to continue.',
  ROTATION: 'Your password has expired. Set a new one to continue.',
};

export function ChangePasswordForm({
  token,
  reason,
  workspaceSlug,
  onChanged,
}: {
  token: string;
  reason: 'POLICY' | 'ROTATION';
  workspaceSlug?: string | null;
  onChanged: () => void;
}) {
  const change = useChangePassword();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });

  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;
  const tooShort = form.next.length > 0 && form.next.length < 8;
  const canSubmit =
    form.current.length > 0 && form.next.length >= 8 && form.next === form.confirm;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await change.mutateAsync({
        token,
        current_password: form.current,
        new_password: form.next,
        ...(workspaceSlug ? { workspace_slug: workspaceSlug } : {}),
      });
      onChanged();
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div>
      <GateHeading title="Update your password" subtitle={CHANGE_REASON[reason]} />
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <Label htmlFor="cp-current">Current password</Label>
          <PasswordInput
            id="cp-current"
            autoComplete="current-password"
            required
            placeholder="Enter your current password"
            value={form.current}
            onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="cp-next">New password</Label>
          <PasswordInput
            id="cp-next"
            autoComplete="new-password"
            required
            placeholder="At least 8 characters"
            value={form.next}
            onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
          />
          {tooShort && (
            <p className="mt-1 text-lg text-red-600 dark:text-red-400">
              Use at least 8 characters.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="cp-confirm">Confirm new password</Label>
          <PasswordInput
            id="cp-confirm"
            autoComplete="new-password"
            required
            placeholder="Re-enter the new password"
            value={form.confirm}
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
          />
          {mismatch && (
            <p className="mt-1 text-lg text-red-600 dark:text-red-400">Passwords don&apos;t match.</p>
          )}
        </div>

        {change.error && (
          <div className={ERROR_BOX} role="alert">
            <PasswordErrorMessage
              error={change.error}
              fallback="Couldn't update your password. Please try again."
            />
          </div>
        )}

        <Button type="submit" className="w-full" disabled={change.isPending || !canSubmit}>
          {change.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </div>
  );
}
